import { Howl } from "howler";
import { GameServices } from "game/GameServices";
import { Tween } from "@westpierstudio/luma/createjs";
import { DragonsVsPandasGameModel, GameState } from "game/DragonsVsPandasGameModel";

const WW_HITS = [`wwHit1`, `wwHit2`, `wwHit3`, `wwHit4`, `wwHit5`, `wwHit6`, `wwHit7`, `wwHit8`];
const WW_HIT_MULTS = [`wwHitMult2`, `wwHitMult3`, `wwHitMult4`, `wwHitMult5`];
const SYMBOL_ID_ORDER = [`WW`, `M1`, `M2`, `M3`, `M4`];

const DEFAULT_FADE = 1000;
const DEFAULT_FAST_FADE = 100;

enum SoundInstance {
    STOP = `stop`,
    PUSH = `push`,
    CHECK = `check`,
    FADE = `fade`
}

interface SoundPlayParams {
    loop?: boolean;
    volume?: number;
    instance?: SoundInstance;
    instanceFadeDuration?: number;
}

type SoundSettings = {
    config: {
        vsMusicVolume: number,
        fgMusicVolume: number,
        vsMusicVolumeFade: number,
        fgMusicVolumeFade: number,
        fgMusicFadeInTime: number,
        fgMusicFadeOutTime: number,
        musicFadeTime: number
    },
    data: {[key: string]: {"name": string, "volume": number}}
}

type QueueSound = {
    key: string,
    params: SoundPlayParams
}

class Sound {
    _filename: string;

    private _services: GameServices;
    private _howls: Howl[] = [];

    private _volume: number;

    constructor(services: GameServices, filename: string, volume: number) {
        this._services = services;
        this._filename = filename;
        this._volume = volume;
    }

    play(params: SoundPlayParams): void {

        if (params.instance) {
            switch (params.instance) {
                case SoundInstance.STOP:
                    this.stop();
                    break;
                case SoundInstance.CHECK:
                    if (this.getPlaying()) { return; };
                    break;
                case SoundInstance.FADE:
                    this.fadeOut(params.instanceFadeDuration ? params.instanceFadeDuration : DEFAULT_FADE);
                    break;
            }
        } else {
            this.fadeOut(DEFAULT_FAST_FADE);
        }

        const howl = new Howl({
            src: this._services.resources.getAudio(this._filename + `.m4a`)
        });
        const howlIndex = this._howls.push(howl) - 1;

        this.loop(params.loop ? params.loop : false, howlIndex);

        this.volume(params.volume ? params.volume : this._volume, howlIndex);

        howl.play();
        howl.on(`end`, () => this.unloadHowl(howl, false));
    }

    stop(index?: number | undefined): void {
        if (index !== undefined) {
            this._howls[index].stop();
            this.unloadHowl(index, true);
            return;
        }

        for (var i = 0; i < this._howls.length; i++) {
            this._howls[0].stop();
            this.unloadHowl(0, true);
        }
    }

    volume(volume: number, index?: number | undefined): void {
        if (index !== undefined) {
            this._howls[index].volume(volume/100);
            return;
        }

        for (var i = 0; i < this._howls.length; i++) {
            this._howls[i].volume(volume/100);
        }
    }

    loop(loop: boolean, index?: number | undefined): void {
        if (index !== undefined) {
            this._howls[index].loop(loop);
            return;
        }

        for (var i = 0; i < this._howls.length; i++) {
            this._howls[i].loop(loop);
        }
    }

    fade(from: number, to: number, duration: number, index?: number | undefined): void {
        if (index !== undefined) {
            this._howls[index].fade(from/100, to/100, duration);
            return;
        }

        for (var i = 0; i < this._howls.length; i++) {
            this._howls[i].fade(from/100, to/100, duration);
        }
    }

    fadeIn(volume: number, duration: number, index?: number | undefined): void {
        this.fade(0, volume, duration, index);
    }

    fadeOut(duration: number, index?: number | undefined): void {
        if (index !== undefined) {
            this._howls[index].fade(this._howls[index].volume(), 0, duration);
            return;
        }

        for (var i = 0; i < this._howls.length; i++) {
            this._howls[i].fade(this._howls[i].volume(), 0, duration);
        }
    }

    private unloadHowl(howl: Howl | number, force: boolean=true): void {
        let index;
        if (typeof howl === `number`) {
            index = howl;
            howl = this._howls[howl];
        } else {
            index = this._howls.indexOf(howl);
        }

        if (force || !howl.loop()) {
            this._howls.splice(index, 1);
            howl.unload();
        }
    }

    getTotalHowls(): number {
        return this._howls.length;
    }

    getVolume(index?: number | undefined): number {
        if (index !== undefined) {
            return this._howls[index].volume() * 100;
        }

        return this._volume;
    }

    getLooping(index?: number | undefined): boolean {
        if (index !== undefined) {
            return this._howls[index].loop();
        }

        for (var i = 0; i < this._howls.length; i++) {
            if (this._howls[i].loop()) { return true; }
        }

        return false;
    }

    getPlaying(): boolean {
        if (this._howls.length > 0) {
            return true;
        }

        return false;
    }
}

class Sounds {
    private services: GameServices;
    private readonly settings: SoundSettings;
    private sounds: Sound[] = [];

    constructor(services: GameServices) {
        this.services = services;

        this.settings = this.services.resources.getSetting(`sounds`);
    }

    play(key:string, params: SoundPlayParams={}): Sound | undefined {

        const sound = this.getSound(key);

        if (sound) {

            sound.play(params);

            return sound;
        }
    }

    stop(): void {
        for (var i = 0; i < this.sounds.length; i++) {
            this.sounds[i].stop();
        }
    }

    getSound(key: string): Sound | undefined {

        if (this.services.audio.soundReady === true) {

            const {name, volume} = this.settings.data[key];

            for (var i = 0; i < this.sounds.length; i++) {
                if (this.sounds[i]._filename === name) {
                    return this.sounds[i];
                }
            }

            const sound = new Sound(this.services, name, volume);
            this.sounds.push(sound);

            return sound;
        }
    }

    getTotalHowls(): number {
        let total = 0;
        for (var i = 0; i < this.sounds.length; i++) {
            total += this.sounds[i].getTotalHowls();
        }

        return total;
    }
}

export class SoundPlayer {
    private readonly settings: SoundSettings;
    private winSoundObject = {};
    private sounds: Sounds;
    private vsMusicVolume!: number;
    private fgMusicVolume!: number;
    private playingFG: boolean = false;
    private miniJackpot: boolean = false;

    playVSOnLoad: boolean = false;
    playFGOnLoad: boolean = false;

    constructor(protected readonly services: GameServices, protected readonly gameModel: DragonsVsPandasGameModel) {
        this.settings = this.services.resources.getSetting(`sounds`);

        this.sounds = new Sounds(services);

        this.vsMusicVolume = this.settings.config.vsMusicVolume;
        this.fgMusicVolume = this.settings.config.fgMusicVolume;
    }

    reelStop(): void {
        this.play(`reelStop`);
    }

    consolation(): void {
        this.play(`consolation`);
    }

    bonusBell(): void {
        this.play(`bonusBell`);
    }

    bigWinCountUp(): void {
        this.dropVSMusic();
        this.dropFGMusic();

        this.play(`bigWinCountUp`, {loop: true, instance: SoundInstance.CHECK});
    }

    bigWinSting(): void {
        this.play(`bigWinSting`);
    }

    stopBigWinCountUp(): void {
        this.pickupVSMusic();
        this.pickupFGMusic();

        this.stop(`bigWinCountUp`);
    }

    uiClick(): void {
        this.play(`uiClick`);
    }

    coinHit1(): void {
        this.play(`coinHit1`);
    }

    coinHit2(): void {
        this.play(`coinHit2`);
    }

    coinHit3(): void {
        this.play(`coinHit3`);
    }

    jumpingWild(): void {
        this.play(`jumpingWild`, {instance: SoundInstance.CHECK});
    }

    jumpingWildHit(hitNum: number): void {
        this.play(WW_HITS[hitNum - 1]);
    }

    jumpingWildHitMult(multiplier: number): void {
        this.play(`wwHitMult`);
        if (multiplier) {
            this.play(WW_HIT_MULTS[multiplier - 2]);
        }
    }

    overlayPassby(): void {
        this.play(`overlayPassby`, {instance: SoundInstance.PUSH});
    }

    coinFlying(): void {
        this.play(`coinFlying`, {instance: SoundInstance.PUSH});
    }

    coinFlyingTerm(): void {
        this.stop(`coinFlying`);
        this.play(`coinFlyingTerm`, {instance: SoundInstance.PUSH});
    }

    coinBowlTouch(): void {
        this.play(`coinBowlTouch`, {instance: SoundInstance.PUSH});
    }

    win(winSymbols: string[]): void {

        let lowestIndex = Number.POSITIVE_INFINITY;

        winSymbols.forEach( (symbol) => {

            if (SYMBOL_ID_ORDER.includes(symbol)) {

                const index = SYMBOL_ID_ORDER.indexOf(symbol);
                if (index < lowestIndex) {

                    lowestIndex = index;
                }
            }
        });

        if (SYMBOL_ID_ORDER.length > lowestIndex) {

            this.play(SYMBOL_ID_ORDER[lowestIndex].toLowerCase());
        }
    }

    transitionIn(): void {
        this.play(`transitionIn`);
    }

    transitionOut(): void {
        this.play(`transitionOut`);
    }

    skipTransition(): void {
        this.stop(`transitionIn`);
        this.stop(`transitionOut`);
        this.stop(`dragonRoar`);
        this.stop(`pandaRoar`);
    }

    dragonRoar(inAnimLoop: boolean): void {
        if (inAnimLoop) {
            this.play(`dragonRoar`, {instance: SoundInstance.CHECK});
        } else {
            this.play(`dragonRoar`, {instance: SoundInstance.FADE});
        }
    }

    pandaRoar(inAnim: boolean): void {
        if (inAnim) {
            this.play(`pandaRoar`, {instance: SoundInstance.CHECK});
        } else {
            this.play(`pandaRoar`, {instance: SoundInstance.FADE});
        }
    }

    vsMusic(): void {

        this.vsMusicVolume = this.settings.config.vsMusicVolume;
        this.play(`vsMusic`, {loop: true, volume: this.vsMusicVolume});
    }

    dropVSMusic(): void {

        if (this.vsMusicVolume !== this.settings.config.vsMusicVolume) {

            return;
        }

        const sound = this.sounds.getSound(`vsMusic`);
        if (sound) {
            this.vsMusicVolume = this.settings.config.vsMusicVolumeFade;
            sound.fade(this.settings.config.vsMusicVolume, this.vsMusicVolume, this.settings.config.musicFadeTime);
        }
    }

    pickupVSMusic(): void {

        if (this.vsMusicVolume !== this.settings.config.vsMusicVolumeFade) {
            return;
        }

        const sound = this.sounds.getSound(`vsMusic`);
        if (sound) {
            this.vsMusicVolume = this.settings.config.vsMusicVolume;
            sound.fade(this.settings.config.vsMusicVolumeFade, this.vsMusicVolume, this.settings.config.musicFadeTime);
        }
    }

    vsMusicTermDragon(): void {
        this.playVSOnLoad = false;
        this.stop(`vsMusic`);
        this.play(`vsMusicTermDragon`);
    }

    vsMusicTermPanda(): void {
        this.playVSOnLoad = false;
        this.stop(`vsMusic`);
        this.play(`vsMusicTermPanda`);
    }

    vsTearDragon(): void {
        this.play(`vsTearDragon`);
    }

    vsTearPanda(): void {
        this.play(`vsTearPanda`);
    }

    vsPick(): void {
        this.play(`vsPick`);
        Tween.removeTweens(this);
    }

    summaryWindowIn(): void {
        this.play(`summaryWindowIn`);
    }

    vsReelsDragon(): void {
        this.play(`vsReelsDragon`, {loop: true});
    }

    vsReelsPanda(): void {
        this.play(`vsReelsPanda`, {loop: true});
    }

    vsReelsTerm(): void {
        const sound = this.sounds.getSound(`vsReelsDragon`);
        if (sound?.getPlaying()) {
            this.stop(`vsReelsDragon`);
            this.play(`vsReelsDragonTerm`);
        } else {
            this.stop(`vsReelsPanda`);
            this.play(`vsReelsPandaTerm`);
        }
    }

    vsIntro(): void {
        this.play(`vsIntro`);
    }

    vsPickPromptLoop(): void {
        Tween.get(this).wait(10000)
        .call(() => {
            this.play(`vsPickPrompt`);
            this.vsPickPromptLoop();
        });
    }

    vsPickDragon(): void {
        this.stop(`vsIntro`);
        this.stop(`vsPickPrompt`);
        this.play(`vsPickDragon`);
    }

    vsPickPanda(): void {
        this.stop(`vsIntro`);
        this.stop(`vsPickPrompt`);
        this.play(`vsPickPanda`);
    }

    vsRespin(): void {
        this.play(`vsRespin`);
    }

    vsWinDragon(): void {
        this.play(`vsWinDragon`);
    }

    vsWinPanda(): void {
        this.play(`vsWinPanda`);
    }

    vsDraw(): void {
        this.play(`vsDraw`);
    }

    fgIncrement(): void {
        this.play(`fgIncrement`, {instance: SoundInstance.PUSH});
    }

    fgMusic(): void {
        this.playingFG = true;

        this.fgMusicVolume = this.settings.config.fgMusicVolume;
        this.play(`fgMusic`, {loop: true, volume: this.fgMusicVolume, instance:SoundInstance.STOP});
    }

    dropFGMusic(): void {
        if (this.fgMusicVolume !== this.settings.config.fgMusicVolume || !this.playingFG) {
            return;
        }

        const sound = this.sounds.getSound(`fgMusic`);
        if (sound) {
            this.fgMusicVolume = this.settings.config.fgMusicVolumeFade;
            sound.fade(this.settings.config.fgMusicVolume, this.fgMusicVolume, this.settings.config.musicFadeTime);
        }
    }

    pickupFGMusic(): void {
        if (this.fgMusicVolume !== this.settings.config.fgMusicVolumeFade || !this.playingFG) {
            return;
        }

        const sound = this.sounds.getSound(`fgMusic`);
        if (sound) {
            this.fgMusicVolume = this.settings.config.fgMusicVolume;
            sound.fade(this.settings.config.fgMusicVolumeFade, this.fgMusicVolume, this.settings.config.musicFadeTime);
        }
    }

    fgMusicTerm(): void {
        this.playFGOnLoad = false;
        this.playingFG = false;

        const sound = this.sounds.getSound(`fgMusic`);
        sound?.fadeOut(this.settings.config.fgMusicFadeOutTime);
    }

    fgMusicMute(): void {
        this.playingFG = false;

        this.fgMusicVolume = 0;
        const sound = this.sounds.getSound(`fgMusic`);
        sound?.volume(0);
    }

    fgMusicUnmute(): void {
        this.playingFG = true;

        this.fgMusicVolume = this.settings.config.fgMusicVolume;
        const sound = this.sounds.getSound(`fgMusic`);

        if (!sound?.getPlaying()) {

            this.fgMusic();
        }
        sound?.fadeIn(this.fgMusicVolume, this.settings.config.fgMusicFadeInTime);
    }

    winRollUp(stopDelay: number, freeSpins: boolean, miniJackpot: boolean = false): void {
        this.miniJackpot = miniJackpot;

        this.dropVSMusic();

        if (this.playingFG) {

            this.dropFGMusic();
        }

        const soundkey = miniJackpot ? `bigWinCountUp` : freeSpins ? `freeSpinWinRollUp` : `baseWinRollUp`;
        this.play(soundkey, {loop: true});

        Tween.get(this.winSoundObject)
        .wait(stopDelay)
        .call(() => {

            this.stop(soundkey);
            this.play(this.miniJackpot ? `bigWinSting` : freeSpins ? `freeSpinWinRollUpTerm` : `baseWinRollUpTerm`);
            this.pickupVSMusic();
            this.pickupFGMusic();
        });
    }

    stopWinRollUp(bigWin: boolean, freeSpins: boolean): void {
        this.pickupVSMusic();
        this.pickupFGMusic();

        Tween.removeTweens(this.winSoundObject);

        this.stop(this.miniJackpot ? `bigWinCountUp` : freeSpins ? `freeSpinWinRollUp` : `baseWinRollUp`);

        if (!bigWin) {
            this.play(this.miniJackpot ? `bigWinSting` : freeSpins ? `freeSpinWinRollUpTerm` : `baseWinRollUpTerm`);
        }
    }

    reset(): void {

        this.sounds.stop();

        Tween.removeTweens(this.winSoundObject);
    }

    onSoundsLoaded(): void {

        if (this.gameModel.state === GameState.VS_SPIN || this.playVSOnLoad) {

            this.vsMusic();
        } else if (this.gameModel.state === GameState.FREE_SPIN || this.playFGOnLoad) {

            this.fgMusic();
        }
    }

    private play(key:string, params: SoundPlayParams={}): Sound | undefined {

        return this.sounds.play(key, params);
    }

    private stop(key: string): Sound | undefined {

        const sound = this.sounds.getSound(key);
        if (sound) {

            sound.stop();

            return sound;
        }
    }
}